import type { ReactNode } from 'react';
import type { TaskPageCount } from '@erp/rpc';
import type {
  RouterNavEntry,
  SidebarNavGroup,
} from '@/routing/nav-from-router';

/**
 * Pure merge logic for org-configurable task pages in the sidebar. Kept free
 * of React/query imports so it stays trivially unit-testable (mirrors
 * taskQueueSearch.ts).
 */

/**
 * Stable id for the generic "Pages" section (pages with a null or unmatched
 * navGroup). Distinct from route-derived group ids (those are group titles)
 * so collapse-state persistence can never collide with a same-named group.
 */
export const TASK_PAGES_FALLBACK_GROUP_ID = '__task-pages__';

export const TASK_PAGES_FALLBACK_GROUP_TITLE = 'Pages';

/**
 * Sidebar group id for an org-created page section. Prefixed so it can never
 * collide with a route-derived group id (a title) or a user layout section id.
 */
export const PAGE_SECTION_GROUP_ID_PREFIX = 'page-section:';

export function pageSectionGroupId(sectionId: string): string {
  return `${PAGE_SECTION_GROUP_ID_PREFIX}${sectionId}`;
}

/** The nav-relevant slice of an org-created page section. */
export type PageSectionNavGroup = {
  id: string;
  name: string;
  sortOrder: number;
};

/** The nav-relevant slice of a TaskPageResponse plus its prebuilt nav entry. */
export type TaskPageNavItem = {
  entry: RouterNavEntry;
  /** Built-in (route-derived) section title the page targets. */
  navGroup: string | null;
  /** Org-created section the page targets; wins over `navGroup` when set. */
  pageSectionId: string | null;
  sortOrder: number;
};

/** Above this the badge reads "99+" so it can't crowd out the page name. */
export const TASK_PAGE_COUNT_BADGE_MAX = 99;

/** Badge text for a count: the number, capped as "99+". */
export function formatTaskPageCount(count: number): string {
  return count > TASK_PAGE_COUNT_BADGE_MAX
    ? `${TASK_PAGE_COUNT_BADGE_MAX}+`
    : String(count);
}

/**
 * What the sidebar shows for a page's open tasks:
 *  - `mine`   — the caller has open tasks on this page: show how many.
 *  - `others` — nothing assigned to the caller but the page isn't empty:
 *               show a quiet marker so the per-actor breakdown has an anchor.
 *  - `null`   — no open tasks (or counts not loaded): show nothing.
 */
export type TaskPageBadgeState =
  { kind: 'mine'; label: string } | { kind: 'others' };

export function taskPageBadgeState(
  count: Pick<TaskPageCount, 'mine' | 'total'> | undefined,
): TaskPageBadgeState | null {
  if (!count) return null;
  if (count.mine > 0) {
    return { kind: 'mine', label: formatTaskPageCount(count.mine) };
  }
  if (count.total > 0) return { kind: 'others' };
  return null;
}

/**
 * Nav entry for one task page. `icon` and `badge` are prebuilt nodes so this
 * stays JSX-free.
 */
export function taskPageNavEntry({
  page,
  icon,
  badge = null,
}: {
  page: { id: string; name: string };
  icon: ReactNode;
  badge?: ReactNode;
}): RouterNavEntry {
  return {
    id: `task-page:${page.id}`,
    fullPath: `/pages/${page.id}`,
    meta: { nav: { label: page.name, icon, badge } },
  };
}

function sortTaskPageItems(items: TaskPageNavItem[]): TaskPageNavItem[] {
  return items
    .slice()
    .sort(
      (a, b) =>
        a.sortOrder - b.sortOrder ||
        (a.entry.meta.nav?.label ?? '').localeCompare(
          b.entry.meta.nav?.label ?? '',
        ),
    );
}

function sortPageSections(
  pageSections: readonly PageSectionNavGroup[],
): PageSectionNavGroup[] {
  return pageSections
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

/**
 * Appends task-page nav entries to the rendered sidebar groups. Placement, in
 * order of precedence:
 *  1. `pageSectionId` naming a known org-created section → the page renders
 *     under that section, which is appended after the route-derived sections
 *     (sections in their own `sortOrder`; a section with no visible page is
 *     not rendered at all).
 *  2. `navGroup` matching the pinned group's title or an existing section
 *     title → appended to that group after the router-derived entries.
 *  3. Anything else (null, an unknown section id, an unmatched title) → a
 *     generic "Pages" section at the very end.
 * Within a group pages sort by `sortOrder` then label. Inputs are never
 * mutated, and an empty page list returns the inputs unchanged — so the
 * sidebar renders router entries immediately and only re-renders once pages
 * load.
 */
export function mergeTaskPagesIntoNav({
  pinnedGroup,
  sections,
  taskPages,
  pageSections = [],
}: {
  pinnedGroup: SidebarNavGroup | null;
  sections: SidebarNavGroup[];
  taskPages: TaskPageNavItem[];
  pageSections?: readonly PageSectionNavGroup[];
}): { pinnedGroup: SidebarNavGroup | null; sections: SidebarNavGroup[] } {
  if (taskPages.length === 0) return { pinnedGroup, sections };

  const byGroupTitle = new Map<string, TaskPageNavItem[]>();
  const bySectionId = new Map<string, TaskPageNavItem[]>();
  const unmatched: TaskPageNavItem[] = [];

  const knownTitles = new Set<string>(
    [pinnedGroup?.title, ...sections.map((s) => s.title)].filter(
      (t): t is string => typeof t === 'string',
    ),
  );
  const knownSectionIds = new Set(pageSections.map((s) => s.id));

  for (const item of taskPages) {
    if (
      item.pageSectionId !== null &&
      knownSectionIds.has(item.pageSectionId)
    ) {
      const arr = bySectionId.get(item.pageSectionId) ?? [];
      arr.push(item);
      bySectionId.set(item.pageSectionId, arr);
    } else if (item.navGroup !== null && knownTitles.has(item.navGroup)) {
      const arr = byGroupTitle.get(item.navGroup) ?? [];
      arr.push(item);
      byGroupTitle.set(item.navGroup, arr);
    } else {
      unmatched.push(item);
    }
  }

  const appendTo = (group: SidebarNavGroup): SidebarNavGroup => {
    const items = byGroupTitle.get(group.title);
    if (!items) return group;
    // The pinned group and a section can share a title only when pinning is
    // customized; buildPinnedGroup folds same-titled sections into the pinned
    // group first, so in practice one target wins. Consume so a page is never
    // rendered twice.
    byGroupTitle.delete(group.title);
    return {
      ...group,
      items: [...group.items, ...sortTaskPageItems(items).map((i) => i.entry)],
    };
  };

  const mergedPinned = pinnedGroup ? appendTo(pinnedGroup) : null;
  const mergedSections = sections.map(appendTo);

  for (const section of sortPageSections(pageSections)) {
    const items = bySectionId.get(section.id);
    if (!items) continue;
    mergedSections.push({
      id: pageSectionGroupId(section.id),
      title: section.name,
      items: sortTaskPageItems(items).map((i) => i.entry),
    });
  }

  if (unmatched.length > 0) {
    mergedSections.push({
      id: TASK_PAGES_FALLBACK_GROUP_ID,
      title: TASK_PAGES_FALLBACK_GROUP_TITLE,
      items: sortTaskPageItems(unmatched).map((i) => i.entry),
    });
  }

  return { pinnedGroup: mergedPinned, sections: mergedSections };
}
